import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, SearchIcon, CheckCircleIcon, ClockIcon, XCircleIcon, MoreHorizontalIcon, EyeIcon, EditIcon, ArchiveIcon, MailIcon, DownloadIcon, CalendarIcon, UserIcon, ChevronRightIcon, FilterIcon, SlidersIcon, StoreIcon, ChevronDownIcon } from 'lucide-react';
// Order status types
export type OrderStatus = 'new' | 'in-progress' | 'open' | 'completed' | 'cancelled';
// Order type definition
export interface Order {
  id: string;
  status: OrderStatus;
  paymentStatus: 'paid' | 'unpaid' | 'cancelled';
  customer: {
    name: string;
    email: string;
    address: string;
    mobileNumber: string;
    additionalPhone?: string;
    address2?: string;
  };
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  total: number;
  date: string;
  time: string;
  transaction?: string;
  transactionStatus?: 'confirmed' | 'pending' | 'failed';
  notes?: string;
  shopId: string;
  shopName: string;
}
// Shop list for filtering
const shopsList = [{
  id: '1',
  name: 'Beach Shop'
}, {
  id: '2',
  name: 'Island Shop'
}, {
  id: '3',
  name: 'ABC'
}];
// Mock order data
const mockOrders: Order[] = [{
  id: '220629-1e753',
  status: 'new',
  paymentStatus: 'paid',
  customer: {
    name: 'randy',
    email: 'randy@randy.com',
    address: 'Kuala Lumpur, Malaysia',
    mobileNumber: '6596605522',
    additionalPhone: '1234',
    address2: '1234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '29/06/2022',
  time: '14:28',
  transaction: '62bc1b4be3c2250009f1e753',
  transactionStatus: 'confirmed',
  shopId: '1',
  shopName: 'Beach Shop'
}, {
  id: '210505-00003',
  status: 'new',
  paymentStatus: 'cancelled',
  customer: {
    name: 'SOWMIYA',
    email: 'sowme76@gmail.com',
    address: 'ADDESS1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 3,
    price: 110.0
  }],
  total: 330.0,
  date: '05/05/2021',
  time: '07:52',
  shopId: '2',
  shopName: 'Island Shop'
}, {
  id: '210506-00004',
  status: 'in-progress',
  paymentStatus: 'paid',
  customer: {
    name: 'sowmiya',
    email: 'sowme76@gmail.com',
    address: 'Address Line 1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '06/05/2021',
  time: '12:48',
  shopId: '1',
  shopName: 'Beach Shop'
}, {
  id: '210504-00002',
  status: 'in-progress',
  paymentStatus: 'cancelled',
  customer: {
    name: 'sowmiya',
    email: 'sowmiya@pomel.com',
    address: 'Address Line 1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '04/05/2021',
  time: '16:06',
  shopId: '3',
  shopName: 'ABC'
}, {
  id: '210503-00001',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'John Doe',
    email: 'john@example.com',
    address: 'Kuala Lumpur, Malaysia',
    mobileNumber: '9876543210'
  },
  items: [{
    name: 'Kittens',
    quantity: 1,
    price: 111.0
  }],
  total: 111.0,
  date: '03/05/2021',
  time: '10:30',
  shopId: '2',
  shopName: 'Island Shop'
}, {
  id: '210502-00005',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'Jane Smith',
    email: 'jane@example.com',
    address: 'Kuala Lumpur, Malaysia',
    mobileNumber: '9876543211'
  },
  items: [{
    name: 'Kittens',
    quantity: 1,
    price: 111.0
  }],
  total: 111.0,
  date: '02/05/2021',
  time: '11:15',
  shopId: '1',
  shopName: 'Beach Shop'
}, {
  id: '210501-00006',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'Alex Brown',
    email: 'alex@example.com',
    address: 'Kuala Lumpur, Malaysia',
    mobileNumber: '9876543212'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '01/05/2021',
  time: '09:45',
  shopId: '3',
  shopName: 'ABC'
}];
export function Orders() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<OrderStatus | 'all'>('all');
  const [shopFilter, setShopFilter] = useState<string>('all');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const getStatusBadge = (status: OrderStatus) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-amber-100 text-amber-800',
      open: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      new: 'New',
      'in-progress': 'In Progress',
      open: 'Open',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-12304" data-mp="1932jbsqvxa3a">
        {labels[status]}
      </span>;
  };
  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon size={14} className="text-green-500" data-id="element-12305" data-mp="1nztnsgff1bzr" />;
      case 'unpaid':
        return <ClockIcon size={14} className="text-amber-500" data-id="element-12306" data-mp="1nxgytjb8q1d7" />;
      case 'cancelled':
        return <XCircleIcon size={14} className="text-red-500" data-id="element-12307" data-mp="1rzc4t14492xb" />;
      default:
        return null;
    }
  };
  const filteredOrders = mockOrders.filter(order => {
    const matchesSearch = searchQuery === '' || order.id.toLowerCase().includes(searchQuery.toLowerCase()) || order.customer.name.toLowerCase().includes(searchQuery.toLowerCase()) || order.customer.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = activeTab === 'all' || order.status === activeTab;
    const matchesShop = shopFilter === 'all' || order.shopId === shopFilter;
    return matchesSearch && matchesStatus && matchesShop;
  });
  const totalAmount = filteredOrders.reduce((sum, order) => sum + order.total, 0);
  const completedAmount = filteredOrders.filter(order => order.status === 'completed').reduce((sum, order) => sum + order.total, 0);
  const cancelledAmount = filteredOrders.filter(order => order.status === 'cancelled').reduce((sum, order) => sum + order.total, 0);
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-12308" data-mp="isa8vy7x07eu">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-12309" data-mp="e1tsq6cpm7os">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12310" data-mp="e08xsfmhgmqe">
          {t('pages.Orders.orders')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-12311" data-mp="90mouidotsne">
          <button onClick={() => window.location.href = '/settings?tab=order-settings'} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-12312" data-mp="16ieay15gfz3q">
            {t('pages.Orders.settings')}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-12313" data-mp="146hj2fy3min">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12314" data-mp="54qeo60iqq4s">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12315" data-mp="sub7rhqdz3rs">
            <ShoppingCartIcon size={16} className="text-gray-500" data-id="element-12316" data-mp="ssi3g938wvmy" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12317" data-mp="xsw1cyb4up79">
              {t('pages.Orders.total_orders')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12318" data-mp="oshdpsap4y0l">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12319" data-mp="or67llts3e6b">
              {filteredOrders.length}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12320" data-mp="34m864n9mtbvi">
            {shopFilter === 'all' ? 'All shops' : shopsList.find(s => s.id === shopFilter)?.name}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12321" data-mp="2x14drl9ezv50">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12322" data-mp="3az2nuegpd5ep">
            <span className="text-gray-500 font-medium" data-id="element-12323" data-mp="360rdm2mhwsvy">$</span>
            <span className="text-sm font-semibold text-gray-900" data-id="element-12324" data-mp="35z7zahkegrwt">
              {t('pages.Orders.total_amount')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12325" data-mp="2ddepho0osd0j">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12326" data-mp="2dairjzou9oc3">
              $
              {totalAmount.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12327" data-mp="1zkp0rbtrwtzq">
            {t('pages.Orders.across')} {filteredOrders.length}{' '}
            {t('components.ShopDashboard.orders')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12328" data-mp="1dn786ip0m8aj">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12329" data-mp="18qql7rtjxzz1">
            <CheckCircleIcon size={16} className="text-green-500" data-id="element-12330" data-mp="13payzwfn1nf2" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12331" data-mp="yrht74i8yvfb">
              {t('components.getOrderStatusBadge.completed')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12332" data-mp="uqyzt60phhb0">
            <div className="text-2xl font-bold text-green-600" data-id="element-12333" data-mp="pye94dd3jkbp">
              $
              {completedAmount.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
            </div>
          </div>
          <div className="px-4 py-2 bg-green-50 text-xs text-green-700 font-medium" data-id="element-12334" data-mp="g22s5a2ka6ax">
            {filteredOrders.filter(o => o.status === 'completed').length}{' '}
            {t('components.ShopDashboard.orders')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12335" data-mp="2inf8lqzozcdm">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12336" data-mp="2mp0gexxdd0gy">
            <XCircleIcon size={16} className="text-red-500" data-id="element-12337" data-mp="2mqv42k00z6oq" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12338" data-mp="2rpeouh2vucfi">
              {t('components.getOrderStatusBadge.cancelled')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12339" data-mp="1yzy1hxu4dlq5">
            <div className="text-2xl font-bold text-red-600" data-id="element-12340" data-mp="1z10npsghyzzq">
              $
              {cancelledAmount.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
            </div>
          </div>
          <div className="px-4 py-2 bg-red-50 text-xs text-red-700 font-medium" data-id="element-12341" data-mp="28vsmv0pq9uwm">
            {filteredOrders.filter(o => o.status === 'cancelled').length}{' '}
            {t('components.ShopDashboard.orders')}
          </div>
        </div>
      </div>

      <div className="space-y-6" data-id="element-12342" data-mp="1l6q8c1qe4i6c">
        {/* Tabs + Controls */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between border-b border-gray-200 gap-4" data-id="element-12343" data-mp="1d6toobeftjk5">
          <div className="flex space-x-6 overflow-x-auto pb-1" role="tablist" aria-label="Order status tabs" data-id="element-12344" data-mp="196jiaxmqgr4r">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'new',
            label: 'New'
          }, {
            id: 'in-progress',
            label: 'In Progress'
          }, {
            id: 'open',
            label: 'Open'
          }, {
            id: 'completed',
            label: 'Completed'
          }, {
            id: 'cancelled',
            label: 'Cancelled'
          }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls="orders-tabpanel" id={`orders-tab-${tab.id}`} onClick={() => setActiveTab(tab.id as OrderStatus | 'all')} className={`pb-3 -mb-px text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === tab.id ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-12345" data-mp="2an8w85bb7si1">
                {tab.label}
              </button>)}
          </div>

          <div className="flex items-center gap-3 pb-3" data-id="element-12346" data-mp="k3hi4gob6679">
            <div className="relative" data-id="element-12347" data-mp="6d5o2c85xxdh">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-12348" data-mp="6c2yq8qho2zo" />

              <input type="search" aria-label={t('pages.Orders.attrs.search_orders')} placeholder={t('pages.Orders.attrs.search_orders')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full sm:w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12349" data-mp="z1tesnboleyf" />
            </div>
            <div className="relative" data-id="element-12350" data-mp="2xcv4e90o7u83">
              <StoreIcon size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-12351" data-mp="2xdwlau4dbaxq" />
              <select value={shopFilter} onChange={e => setShopFilter(e.target.value)} className="appearance-none pl-9 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors cursor-pointer text-gray-700 font-medium" data-id="element-12352" data-mp="3q2277k75r5z5">
                <option value="all" data-id="element-12353" data-mp="cu7hxtevalwt">{t('pages.Orders.all_shops')}</option>
                {shopsList.map(shop => <option key={shop.id} value={shop.id} data-id="element-12354" data-mp="k6x4jch0zxp1">
                    {shop.name}
                  </option>)}
              </select>
              <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-12355" data-mp="u41i4nwt2ku8" />
            </div>{' '}
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-12356" data-mp="18315ztchuvpc">
            <p className="text-gray-500 text-sm" data-id="element-12357" data-mp="1cuj6yheiowab">
              {t('pages.Orders.no_orders_found_matching_your_criteria')}
            </p>
          </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12358" data-mp="1hvgnyqnsjka8">
            <div className="overflow-x-auto" data-id="element-12359" data-mp="1lswdemvoxa0y">
              <table className="w-full text-left border-collapse" data-id="element-12360" data-mp="1qtamm8912q0k">
                <thead data-id="element-12361" data-mp="3mx6vzvwtb5ie">
                  <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-12362" data-mp="3mwozcy68mp9y">
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12363" data-mp="3ivlpcsavfngn">
                      {t('pages.Orders.order')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12364" data-mp="jp738plofd6">
                      {t('pages.Orders.date')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12365" data-mp="ae8r0if8jsvp">
                      {t('pages.Orders.customer')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12366" data-mp="jff9uy5cxfs4">
                      {t('pages.Orders.shop')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12367" data-mp="2l7bk84fsgjlb">
                      {t('components.CampaignList.status')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12368" data-mp="vqzfnk9eai4z">
                      {t('pages.Orders.payment')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12369" data-mp="mye1yikmf1n0">
                      {t('pages.OrderDetail.total')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-12370" data-mp="qxwkj7oyz0mo">
                      {t('pages.CampaignDetails.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-12371" data-mp="40bjynqhq4yp">
                  {filteredOrders.map(order => <tr key={order.id} onClick={() => navigate(`/order-detail/${order.id}`)} className="hover:bg-gray-50 transition-colors cursor-pointer group" data-id="element-12372" data-mp="826m1ug8wmdj">
                      <td className="px-5 py-4" data-id="element-12373" data-mp="22ctmtja5zmbz">
                        <div className="text-sm font-semibold text-gray-900 group-hover:text-[#9F140F] transition-colors" data-id="element-12374" data-mp="q4hqwbvdoods">
                          #{order.id}
                        </div>
                        {order.transaction && <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[120px]" data-id="element-12375" data-mp="gdn3332hhgq8">
                            {order.transaction}
                          </div>}
                      </td>
                      <td className="px-5 py-4" data-id="element-12376" data-mp="2fp3j3stnous">
                        <div className="flex items-center text-sm text-gray-900" data-id="element-12377" data-mp="2ex6dojktj2e">
                          <CalendarIcon size={14} className="mr-1.5 text-gray-400" data-id="element-12378" data-mp="3tnb3alcf776d" />

                          {order.date}
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mt-0.5" data-id="element-12379" data-mp="1rssldff3lnuy">
                          <ClockIcon size={14} className="mr-1.5 text-gray-400" data-id="element-12380" data-mp="1mtz55f8ffir3" />

                          {order.time}
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-12381" data-mp="246fh6lkagjcj">
                        <div className="text-sm font-medium text-gray-900" data-id="element-12382" data-mp="247qmiptdtz5r">
                          {order.customer.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[150px]" data-id="element-12383" data-mp="1cf9miknc7dun">
                          {order.customer.email}
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-12384" data-mp="1ldv7emga74yl">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-12385" data-mp="1qdzmc81jjrmp">
                          <StoreIcon size={11} className="mr-1.5" data-id="element-12386" data-mp="2wtjpp0ilbft2" />
                          {order.shopName}
                        </span>
                      </td>
                      <td className="px-5 py-4" data-id="element-12387" data-mp="hjdhj28b3pm5">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-5 py-4" data-id="element-12388" data-mp="mg3x7og78j71">
                        <div className="flex items-center" data-id="element-12389" data-mp="r8na0j72baas">
                          {getPaymentStatusIcon(order.paymentStatus)}
                          <span className="ml-1.5 text-sm text-gray-900 capitalize" data-id="element-12390" data-mp="w6gdv5kw7s7w">
                            {order.paymentStatus}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-12391" data-mp="3j2p3urcm2yv">
                        <div className="text-sm font-medium text-gray-900" data-id="element-12392" data-mp="8hdzc6lk2fhm">
                          {formatCurrencyAmount(order.total, {
                      fractionDigits: 2
                    })}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right relative" data-id="element-12393" data-mp="1jzez6wppmyom">
                        <div ref={contextMenuOpenId === order.id ? contextMenuRef : null} data-id="element-12394" data-mp="1nx2zyge33lof">
                          <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(contextMenuOpenId === order.id ? null : order.id);
                    }} aria-label="Order actions" data-id="element-12395" data-mp="2znv0lrf5ujtv">
                            <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-12396" data-mp="eyfgffag5td7" />
                          </button>

                          {contextMenuOpenId === order.id && <>
                              <div className="fixed inset-0 z-10" onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                      }} data-id="element-12397" data-mp="t2mrcokhkcrc" />

                              <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-12398" data-mp="p2cfiva0is4k">
                                <button onClick={e => {
                          e.stopPropagation();
                          setContextMenuOpenId(null);
                          navigate(`/order-detail/${order.id}`);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12399" data-mp="1gts7l4uc9726">
                                  <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-12400" data-mp="3kza4ct15jygw" />

                                  {t('pages.Orders.view_details')}
                                </button>
                                <button onClick={e => {
                          e.stopPropagation();
                          setContextMenuOpenId(null);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12401" data-mp="3p0vomzg0nf14">
                                  <MailIcon size={16} className="mr-3 text-gray-400" data-id="element-12402" data-mp="36ansj1aqhouf" />

                                  {t('pages.Orders.email_customer')}
                                </button>
                                <div className="border-t border-gray-100 my-1" data-id="element-12403" data-mp="2r8pdeh5r6n25" />
                                <button onClick={e => {
                          e.stopPropagation();
                          setContextMenuOpenId(null);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-12404" data-mp="2r6mch232b9in">
                                  <ArchiveIcon size={16} className="mr-3" data-id="element-12405" data-mp="3a1b6itp9zzj1" />
                                  {t('pages.Orders.archive_order')}
                                </button>
                              </div>
                            </>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}